import { create } from 'zustand';

export interface PlatformSetting {
  id: string;
  key: string;
  value: string;
  category: string;
  valueType: string;
  label: string;
  description?: string;
  isSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PlatformSettingsState {
  settings: PlatformSetting[];
  loading: boolean;
  error: string | null;
  /** True after the first successful fetch. */
  loaded: boolean;
  /** Tracks keys currently being saved (optimistic UI). */
  savingKeys: Set<string>;

  fetchSettings: (token?: string | null) => Promise<void>;
  updateSetting: (key: string, value: string, token?: string | null) => Promise<void>;
  getByCategory: (category: string) => PlatformSetting[];
  getByKey: (key: string) => PlatformSetting | undefined;
}

export const usePlatformSettingsStore = create<PlatformSettingsState>()((set, get) => ({
  settings: [],
  loading: false,
  error: null,
  loaded: false,
  savingKeys: new Set(),

  fetchSettings: async (token) => {
    set({ loading: true, error: null });
    try {
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch('/api/dotnet/platform-settings', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as PlatformSetting[];
      set({ settings: Array.isArray(data) ? data : [], loaded: true, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  updateSetting: async (key, value, token) => {
    // Optimistic update
    const prev = get().settings;
    set((s) => ({
      settings: s.settings.map((st) =>
        st.key === key ? { ...st, value, updatedAt: new Date().toISOString() } : st,
      ),
      savingKeys: new Set([...s.savingKeys, key]),
    }));

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`/api/dotnet/platform-settings/${key}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ value }),
      });
      if (!res.ok) {
        // Backend liefert bei Contract-Verletzungen (z. B. verwaiste
        // Role-Bindings, letztes LLM-Profil) eine aussagekräftige
        // RuntimeContractErrorResponse { message }. Diese durchreichen,
        // statt sie hinter „HTTP 400" zu verstecken.
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { message?: unknown };
          if (typeof body?.message === 'string' && body.message.trim()) {
            detail = body.message;
          }
        } catch {
          /* kein JSON-Body — HTTP-Status als Fallback behalten */
        }
        throw new Error(detail);
      }
      const updated = (await res.json()) as PlatformSetting;
      set((s) => ({
        settings: s.settings.some((st) => st.key === key)
          ? s.settings.map((st) => (st.key === key ? updated : st))
          : [...s.settings, updated],
        savingKeys: new Set([...s.savingKeys].filter((k) => k !== key)),
      }));
    } catch (err) {
      // Rollback on error — Fehlermeldung erhalten, damit der Aufrufer
      // den echten Grund anzeigen kann.
      set((s) => ({
        settings: prev,
        savingKeys: new Set([...s.savingKeys].filter((k) => k !== key)),
      }));
      throw err instanceof Error ? err : new Error('Speichern fehlgeschlagen');
    }
  },

  getByCategory: (category) => get().settings.filter((s) => s.category === category),

  getByKey: (key) => get().settings.find((s) => s.key === key),
}));
